import { useState, useCallback } from 'react'

const DEFAULT_MAX_ITEMS = 50

interface BulkSelectionState {
  selectedIds: Set<number>
  limitReached: boolean
  selectAllCapped: boolean
  cappedTotal: number | null
}

interface BulkSelectionActions {
  toggle: (id: number) => void
  selectAll: (ids: number[], total: number) => void
  selectPage: (ids: number[]) => void
  clear: () => void
}

export function useBulkSelection(
  maxItems = DEFAULT_MAX_ITEMS,
): BulkSelectionState & BulkSelectionActions {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [selectAllCapped, setSelectAllCapped] = useState(false)
  const [cappedTotal, setCappedTotal] = useState<number | null>(null)

  const limitReached = selectedIds.size >= maxItems

  const toggle = useCallback(
    (id: number) => {
      setSelectedIds((prev) => {
        const next = new Set(prev)
        if (next.has(id)) {
          next.delete(id)
          return next
        }
        if (next.size >= maxItems) return prev
        next.add(id)
        return next
      })
      setSelectAllCapped(false)
      setCappedTotal(null)
    },
    [maxItems],
  )

  const selectAll = useCallback(
    (ids: number[], total: number) => {
      setSelectedIds(new Set(ids))
      if (total > maxItems) {
        setSelectAllCapped(true)
        setCappedTotal(total)
      } else {
        setSelectAllCapped(false)
        setCappedTotal(null)
      }
    },
    [maxItems],
  )

  const selectPage = useCallback(
    (ids: number[]) => {
      const capped = ids.slice(0, maxItems)
      setSelectedIds(new Set(capped))
      if (ids.length > maxItems) {
        setSelectAllCapped(true)
        setCappedTotal(ids.length)
      } else {
        setSelectAllCapped(false)
        setCappedTotal(null)
      }
    },
    [maxItems],
  )

  const clear = useCallback(() => {
    setSelectedIds(new Set())
    setSelectAllCapped(false)
    setCappedTotal(null)
  }, [])

  return {
    selectedIds,
    limitReached,
    selectAllCapped,
    cappedTotal,
    toggle,
    selectAll,
    selectPage,
    clear,
  }
}
